/**
 * Feedback System Types
 * Types for user feedback collection during beta testing
 */

export type FeedbackCategory =
  | 'bug'
  | 'feature'
  | 'ui_ux'
  | 'data_accuracy'
  | 'performance'
  | 'confusing'
  | 'positive'
  | 'other';

export type FeedbackStatus = 'new' | 'in_review' | 'planned' | 'implemented' | 'dismissed';

export type Platform = 'ios' | 'android' | 'web';

/**
 * User input when submitting feedback
 */
export interface FeedbackInput {
  categories: FeedbackCategory[]; // Multiple categories can be selected
  customCategoryText?: string; // Required if 'other' is in categories
  urgency?: number; // 1-5 star rating
  description: string;
}

/**
 * Auto-captured context information
 */
export interface FeedbackContext {
  screenName?: string;
  appVersion: string;
  deviceInfo: string;
  platform: Platform;
}

/**
 * Complete feedback record from database
 */
export interface Feedback {
  id: string;
  user_id: string;
  user_email: string | null;
  category: FeedbackCategory[]; // Array of categories
  custom_category_text: string | null; // Custom text for 'other' category
  urgency: number | null;
  description: string;
  screen_name: string | null;
  app_version: string | null;
  device_info: string | null;
  platform: Platform | null;
  status: FeedbackStatus;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Feedback category options with display labels (no emojis)
 */
export const FEEDBACK_CATEGORIES = [
  { value: 'bug' as const, label: 'Bug/Crash' },
  { value: 'feature' as const, label: 'Feature Request' },
  { value: 'ui_ux' as const, label: 'UI/UX Issue' },
  { value: 'data_accuracy' as const, label: 'Data Accuracy' },
  { value: 'performance' as const, label: 'Performance' },
  { value: 'confusing' as const, label: 'Confusing' },
  { value: 'positive' as const, label: 'Positive Feedback' },
  { value: 'other' as const, label: 'Other' },
] as const;

/**
 * Urgency level descriptions
 */
export const URGENCY_LABELS = {
  0: 'Optional',
  1: 'Low',
  2: 'Medium',
  3: 'Medium-High',
  4: 'High',
  5: 'Critical',
} as const;
